import {
  Accordion,
  Button,
  cn,
  ConfirmableDeleteButton,
  Draggable,
  Icon,
  useDraggableContext,
} from '@risksmart-app/atomic-ui';
import { type MouseEvent, useEffect, useRef, useState } from 'react';

import type { FieldEditorValues } from '../config';
import type { FieldSummary } from '../constants';
import { useFormEditorDialogStore } from '../useFormEditorDialogStore';
import { FieldCard } from './FieldCard';

export const SectionAccordion = ({
  sectionId,
  title,
  fieldIds,
  containerIds,
  fields,
  fieldConfigs,
  hasConditionalLogic = false,
  addFieldsLabel,
  disallowed = false,
  onEditSection,
  onDeleteSection,
  onEditField,
  onDeleteField,
  onAddField,
  fieldTypeOptions,
}: {
  sectionId: string;
  title: string;
  fieldIds: string[];
  containerIds: string[];
  fields: Record<string, FieldSummary>;
  fieldConfigs?: Record<string, FieldEditorValues>;
  hasConditionalLogic?: boolean;
  addFieldsLabel?: string;
  /**
   * True while the active drag is hovering this section but cannot be
   * dropped (would create a circular dependency). Renders a "cannot drop
   * here" overlay across the section.
   */
  disallowed?: boolean;
  onEditSection: () => void;
  onDeleteSection: () => void;
  onEditField: (fieldId: string) => void;
  onDeleteField: (fieldId: string) => void;
  onAddField: () => void;
  fieldTypeOptions?: { value: string; label: string }[];
}) => {
  const handleActionClick = (e: MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const openSections = useFormEditorDialogStore((s) => s.openSections);
  const isClosed = !openSections.includes(sectionId);

  const { activeId, overId } = useDraggableContext();
  const isFieldDrag =
    activeId != null && !containerIds.includes(String(activeId));
  const isDropTarget =
    isFieldDrag &&
    overId != null &&
    (String(overId) === sectionId || fieldIds.includes(String(overId)));

  const showPeek = isClosed && isDropTarget;

  const [showFlash, setShowFlash] = useState(false);
  const wasDropTarget = useRef(false);
  const wasClosed = useRef(false);

  useEffect(() => {
    if (isDropTarget && isClosed) {
      wasDropTarget.current = true;
      wasClosed.current = true;
    }

    if (activeId == null && wasDropTarget.current && wasClosed.current) {
      wasDropTarget.current = false;
      wasClosed.current = false;
      setShowFlash(true);
      const timer = setTimeout(() => setShowFlash(false), 500);

      return () => clearTimeout(timer);
    }

    if (!isDropTarget) {
      wasDropTarget.current = false;
    }
  }, [activeId, isDropTarget, isClosed]);

  return (
    <Accordion.Item
      value={sectionId}
      variant={'inverse'}
      className={cn(
        'contain-paint transition-shadow duration-200 p-0 relative',
        showPeek &&
          'border-secondary shadow-[0_0_0_1px_oklch(var(--secondary)/0.4)]',
        showFlash && 'animate-drop-flash',
        disallowed &&
          'border-destructive shadow-[0_0_0_1px_oklch(var(--destructive)/0.4)]'
      )}
    >
      {disallowed ? (
        <div
          aria-hidden={'true'}
          className={cn(
            'pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-destructive-minimal/85'
          )}
        >
          <div
            className={cn(
              'flex items-center gap-2 rounded-lg border border-destructive bg-neutral px-3 py-2 shadow-md'
            )}
          >
            <Icon
              name={'alert-triangle'}
              size={'sm'}
              className={'text-destructive'}
            />
            <span
              className={cn(
                'text-sm font-medium text-destructive whitespace-nowrap'
              )}
            >
              {'Cannot drop here — would create a circular dependency'}
            </span>
          </div>
        </div>
      ) : null}
      <Accordion.Header className={'px-3 justify-between group/section-header'}>
        <div className={'flex gap-0 px-0'}>
          <Draggable.DragHandle className={'text-neutral-active'} />
          <Accordion.Trigger
            variant={'inverse'}
            className={'shrink flex-0 gap-2'}
          >
            {title}
            {hasConditionalLogic ? (
              <span
                title={'This section has conditional logic applied'}
                className={cn('flex h-full justify-center items-center')}
              >
                <Icon name={'data-flow-03'} size={'sm'} />
              </span>
            ) : null}
          </Accordion.Trigger>
        </div>
        <div
          className={cn(
            'flex items-center gap-2 opacity-0 group-hover/section-header:opacity-100 transition-opacity'
          )}
        >
          <Button
            variant={'neutral'}
            style={'ghost'}
            size={'sm'}
            aria-label={'Edit section'}
            className={'p-0'}
            onClick={(e) => handleActionClick(e, onEditSection)}
          >
            <Icon name={'settings-04'} size={'sm'} />
          </Button>
          <ConfirmableDeleteButton onConfirm={onDeleteSection} size={'sm'} />
        </div>
      </Accordion.Header>
      <Accordion.Content variant={'inverse'} className={cn('p-4')}>
        <div className={cn('flex flex-col gap-3')}>
          {fieldIds.map((fieldId) => {
            const field = fields[fieldId];
            // A group (e.g. "Test configuration") is a layout marker wrapping
            // several real fields, not a field of its own: `FieldGroup` renders
            // its children verbatim and nothing reads a form_field_configuration
            // row keyed by the group id, so editing one persists settings that
            // never apply. Groups stay draggable for ordering only (RSP-6644).
            const isGroupField = field?.isGroup === true;

            return (
              <Draggable.Item key={fieldId} id={fieldId}>
                <FieldCard
                  name={field?.name ?? fieldId}
                  type={field?.type ?? 'text'}
                  required={field?.required ?? false}
                  readOnly={field?.readOnly ?? false}
                  hidden={field?.hidden ?? false}
                  hasConditionalLogic={
                    !!(
                      fieldConfigs?.[fieldId]?.conditionalLogicEnabled &&
                      (fieldConfigs[fieldId]?.conditionalLogicRules?.length ??
                        0) > 0
                    )
                  }
                  subtitle={field?.subtitle}
                  fieldTypeOptions={fieldTypeOptions}
                  onEdit={isGroupField ? undefined : () => onEditField(fieldId)}
                  onDelete={
                    field?.canDelete !== false
                      ? () => onDeleteField(fieldId)
                      : undefined
                  }
                />
              </Draggable.Item>
            );
          })}
          {addFieldsLabel ? (
            <Button
              style={'dashed-fill'}
              radius={'xl'}
              className={'w-full'}
              onClick={onAddField}
            >
              <Icon name={'plus'} size={'sm'} />
              {addFieldsLabel}
            </Button>
          ) : null}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};
